import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { LayoutDashboard, Receipt, Upload, Building2, FileText, Plus, LogOut, ChevronDown, User, Settings, FolderArchive, Users, UserCog, FileInput, FilePenLine, Scale, CreditCard, Landmark, Car, ShoppingCart } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import NotificationBell from "./NotificationBell";
import api from "@/lib/api";

const NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/transactions", label: "Transactions", icon: Receipt },
  { path: "/upload", label: "Upload", icon: Upload },
  { path: "/bank-connections", label: "Bank Connections", icon: Landmark },
  { path: "/accounts", label: "Accounts", icon: Building2 },
  { path: "/customers", label: "Customers", icon: Users },
  { path: "/invoices", label: "Invoices", icon: FileInput },
  { path: "/quotes", label: "Quotes", icon: FilePenLine },
  { path: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart },
  { path: "/expenses", label: "Travel & Expenses", icon: Car },
  { path: "/governance", label: "Governance", icon: Scale },
  { path: "/documents", label: "Documents", icon: FolderArchive },
  { path: "/reports", label: "Reports", icon: FileText },
  { path: "/team", label: "Team", icon: UserCog },
  { path: "/subscription", label: "Subscription", icon: CreditCard },
  { path: "/settings", label: "Settings", icon: Settings },
];

export default function SidebarLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(localStorage.getItem("selected_company") || "");
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyEin, setNewCompanyEin] = useState("");
  const [newCompanyType, setNewCompanyType] = useState("Sole Proprietorship");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const res = await api.get("/companies");
      setCompanies(res.data);
      if (res.data.length > 0) {
        const saved = localStorage.getItem("selected_company");
        const found = res.data.find(c => c.company_id === saved);
        if (found) {
          setSelectedCompanyId(found.company_id);
        } else {
          setSelectedCompanyId(res.data[0].company_id);
          localStorage.setItem("selected_company", res.data[0].company_id);
        }
      }
    } catch (err) {
      console.error("Failed to load companies:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (val) => {
    if (val === "__new__") {
      setShowNewCompany(true);
      return;
    }
    setSelectedCompanyId(val);
    localStorage.setItem("selected_company", val);
  };

  const createCompany = async () => {
    if (!newCompanyName.trim()) return;
    try {
      const res = await api.post("/companies", {
        name: newCompanyName,
        ein: newCompanyEin,
        business_type: newCompanyType,
      });
      setCompanies(prev => [...prev, res.data]);
      setSelectedCompanyId(res.data.company_id);
      localStorage.setItem("selected_company", res.data.company_id);
      setShowNewCompany(false);
      setNewCompanyName("");
      setNewCompanyEin("");
    } catch (err) {
      console.error("Failed to create company:", err);
    }
  };

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.clear();
    navigate("/login");
  };

  const selectedCompany = companies.find(c => c.company_id === selectedCompanyId);

  return (
    <div className="min-h-screen bg-background flex" data-testid="sidebar-layout">
      {/* Sidebar */}
      <aside className="w-64 border-r border-[#3d4654]/50 flex flex-col fixed h-screen gradient-sidebar overflow-hidden">
        {/* Logo - Fixed at top */}
        <div className="p-4 flex items-center flex-shrink-0">
          <img 
            src="/images/bizledger-logo.png" 
            alt="BizLedger Pro" 
            className="h-28 w-auto object-contain"
          />
        </div>

        <Separator className="bg-[#3d4654]/50 flex-shrink-0" />

        {/* Company Switcher - Fixed */}
        <div className="p-4 flex-shrink-0">
          <Label className="text-xs text-[#6b7280] uppercase tracking-wider mb-2 block">Company</Label>
          <Select value={selectedCompanyId} onValueChange={handleCompanyChange} data-testid="company-switcher">
            <SelectTrigger className="bg-[#232b3e] border-[#3d4654] text-[#d4c4b0] w-full" data-testid="company-switcher-trigger">
              <SelectValue placeholder="Select company" />
            </SelectTrigger>
            <SelectContent className="bg-[#232b3e] border-[#3d4654]">
              {companies.map(c => (
                <SelectItem key={c.company_id} value={c.company_id} className="text-[#d4c4b0]">
                  {c.name}
                </SelectItem>
              ))}
              <SelectItem value="__new__" className="text-[#e85d24]">
                <span className="flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add Company</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Separator className="bg-[#3d4654]/50 flex-shrink-0" />

        {/* Nav Links - Scrollable */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0" data-testid="sidebar-nav">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-[#e85d24]/10 text-[#e85d24] border-l-2 border-[#e85d24]'
                    : 'text-[#8b9299] hover:text-[#d4c4b0] hover:bg-[#232b3e]'
                }`
              }
            >
              <item.icon className="w-4.5 h-4.5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User section with notifications - Fixed at bottom */}
        <div className="p-4 border-t border-[#3d4654]/50 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-[#6b7280] uppercase tracking-wide">Account</span>
            {selectedCompanyId && <NotificationBell companyId={selectedCompanyId} />}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full p-2 rounded-md hover:bg-[#232b3e] transition-colors" data-testid="user-menu-trigger">
                <div className="w-8 h-8 rounded-full bg-[#e85d24]/20 flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <User className="w-4 h-4 text-[#e85d24]" />
                  )}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm text-[#d4c4b0] truncate">{user?.name || "User"}</p>
                  <p className="text-xs text-[#6b7280] truncate">{user?.email || ""}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-[#6b7280]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-[#232b3e] border-[#3d4654]">
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 min-h-screen gradient-mesh-app">
        <div className="p-6 md:p-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#e85d24] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : companies.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[60vh] gap-4" data-testid="no-company-prompt">
              <Building2 className="w-16 h-16 text-[#6b7280]" />
              <h2 className="text-2xl font-heading font-bold text-[#d4c4b0]">Welcome to BizLedger Pro</h2>
              <p className="text-[#8b9299] text-center max-w-md">
                Create your first company to start tracking finances and classifying transactions.
              </p>
              <Button onClick={() => setShowNewCompany(true)} className="btn-primary-gradient" data-testid="create-first-company-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Company
              </Button>
            </div>
          ) : (
            typeof children === 'function' 
              ? children({ companyId: selectedCompanyId, company: selectedCompany, companies, refreshCompanies: loadCompanies, onNavigate: (path) => navigate(`/${path}`) }) 
              : children
          )}
        </div>
      </main>

      {/* New Company Dialog */}
      <Dialog open={showNewCompany} onOpenChange={setShowNewCompany}>
        <DialogContent className="bg-card border-border" data-testid="new-company-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">Create Company</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-muted-foreground text-sm">Company Name *</Label>
              <Input value={newCompanyName} onChange={(e) => setNewCompanyName(e.target.value)}
                placeholder="Acme Corp" className="mt-1.5 bg-secondary border-border text-white"
                data-testid="new-company-name-input" />
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">EIN (Optional)</Label>
              <Input value={newCompanyEin} onChange={(e) => setNewCompanyEin(e.target.value)}
                placeholder="XX-XXXXXXX" className="mt-1.5 bg-secondary border-border text-white"
                data-testid="new-company-ein-input" />
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Business Type</Label>
              <Select value={newCompanyType} onValueChange={setNewCompanyType}>
                <SelectTrigger className="mt-1.5 bg-secondary border-border text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {["Sole Proprietorship", "LLC", "S-Corp", "C-Corp", "Partnership"].map(t => (
                    <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCompany(false)} className="border-border text-white">Cancel</Button>
            <Button onClick={createCompany} disabled={!newCompanyName.trim()} data-testid="create-company-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
